import React, { useState, useEffect, createContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from 'components/ui/Header';
import Icon from 'components/AppIcon';
import { supabase } from 'lib/supabase';
import Footer from 'pages/landing-page/components/Footer';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from 'contexts/AuthContext';

// Create context for KPI refresh
export const KPIRefreshContext = createContext(null);

const AdminDashboardLayout = ({ children }) => {
  const location = useLocation();
  const { userProfile } = useAuth();
  const isAdminOrSuperadmin = userProfile?.role === 'admin' || userProfile?.role === 'superadmin';
  const [kpiData, setKpiData] = useState({
    totalClienti: 0,
    totalProfessionisti: 0,
    verifiedProfessionisti: 0,
    pendingProfessionisti: 0,
    pendingCategorieRichieste: 0
  });

  // ── Heartbeat: ping every 60s while admin is on any admin page ──
  useEffect(() => {
    const sendHeartbeat = async () => {
      try {
        const { data: { user } } = await supabase?.auth?.getUser();
        if (!user) return;
        await supabase?.from('admin_heartbeats')?.insert({ admin_id: user?.id });
      } catch (err) {
        // Silently ignore heartbeat errors
      }
    };

    sendHeartbeat(); // immediate ping on mount
    const interval = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchKPIData();
    
    // Set up real-time subscription for pending count updates
    const subscription = supabase
      ?.channel('pending-professionals-changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'users',
          filter: 'role=eq.professionista'
        },
        () => {
          fetchKPIData();
        }
      )
      ?.subscribe();

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const fetchKPIData = async () => {
    try {
      const { data: users, error: usersError } = await supabase
        ?.from('users')
        ?.select('role, verification_status');

      if (usersError) throw usersError;

      const clienti = users?.filter(u => u?.role === 'cliente') || [];
      const professionisti = users?.filter(u => u?.role === 'professionista') || [];
      const verified = professionisti?.filter(p => p?.verification_status === 'approved') || [];
      const pending = professionisti?.filter(p => p?.verification_status === 'pending') || [];

      const { count: pendingCatCount, error: catError } = await supabase
        ?.from('category_requests')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('status', 'pending');

      if (catError) console.error('Error fetching category_requests count:', catError);

      setKpiData({
        totalClienti: clienti?.length,
        totalProfessionisti: professionisti?.length,
        verifiedProfessionisti: verified?.length,
        pendingProfessionisti: pending?.length,
        pendingCategorieRichieste: pendingCatCount ?? 0
      });
    } catch (err) {
      console.error('Error fetching KPI data:', err);
    }
  };

  const navItems = [
    { path: '/admin', label: 'Overview', icon: 'LayoutDashboard' },
    { path: '/admin/clienti', label: 'Clienti', icon: 'Users' },
    { 
      path: '/admin/professionisti', 
      label: 'Professionisti', 
      icon: 'Briefcase',
      badge: kpiData?.pendingProfessionisti > 0 ? kpiData?.pendingProfessionisti : null
    },
    { path: '/admin/richieste', label: 'Richieste', icon: 'FileText' },
    { path: '/admin/preventivi', label: 'Preventivi', icon: 'ClipboardList' },
    { path: '/admin/recensioni', label: 'Recensioni', icon: 'Star' },
    { path: '/admin/pagine-professionisti', label: 'Pagine Pubbliche', icon: 'Globe' },
    { path: '/admin/fatturazione', label: 'Fatturazione', icon: 'Euro' },
    { path: '/admin/programma-vip', label: 'Programma VIP', icon: 'Crown' },
    { path: '/admin/categorie', label: 'Categorie', icon: 'Tag',
      badge: kpiData?.pendingCategorieRichieste > 0 ? kpiData?.pendingCategorieRichieste : null
    },
    { path: '/admin/insolvenze', label: 'Insolvenze', icon: null, customIcon: AlertTriangle },
    { path: '/admin/audit-log', label: 'Audit', icon: 'Shield' },
    { path: '/admin/landing-page', label: 'Landing Page', icon: 'Layout' }
  ];

  const isActive = (path) => {
    if (path === '/admin') {
      return location?.pathname === '/admin';
    }
    return location?.pathname?.startsWith(path);
  };

  return (
    <KPIRefreshContext.Provider value={{ refreshKPIs: fetchKPIData }}>
      <div className="min-h-screen bg-gray-50 flex flex-col pt-16">
        <Header />
        
        <div className="flex flex-1">
          {/* Left Sidebar */}
          <aside className="w-64 bg-white border-r border-gray-200 min-h-[calc(100vh-64px)] sticky top-16 self-start">
            <div className="p-6">
              <h2 className="text-lg font-bold text-gray-900 mb-6">Admin Panel</h2>
              <nav className="space-y-2">
                {navItems?.map((item) => (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 relative ${
                      isActive(item?.path)
                        ? 'bg-blue-50 text-blue-700 font-semibold' :'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {item?.customIcon ? (
                      <item.customIcon
                        size={20}
                        color={isActive(item?.path) ? 'var(--color-primary)' : 'currentColor'}
                      />
                    ) : (
                      <Icon 
                        name={item?.icon} 
                        size={20} 
                        color={isActive(item?.path) ? 'var(--color-primary)' : 'currentColor'}
                      />
                    )}
                    <span>{item?.label}</span>
                    {item?.badge && (
                      <span className="ml-auto bg-yellow-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                        {item?.badge}
                      </span>
                    )}
                  </Link>
                ))}
                <div className="pt-4 mt-4 border-t border-gray-200">
                  {isAdminOrSuperadmin && (
                    <Link
                      to="/admin/pagamenti-rateali"
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 mb-1 ${
                        location?.pathname === '/admin/pagamenti-rateali' ?'bg-blue-50 text-blue-700 font-semibold' :'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <Icon
                        name="CreditCard"
                        size={20}
                        color={location?.pathname === '/admin/pagamenti-rateali' ? 'var(--color-primary)' : 'currentColor'}
                      />
                      <span>Pagamenti rateali</span>
                    </Link>
                  )}
                  <Link
                    to="/admin/settings"
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                      location?.pathname === '/admin/settings' ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon
                      name="Settings"
                      size={20}
                      color={location?.pathname === '/admin/settings' ? 'var(--color-primary)' : 'currentColor'}
                    />
                    <span>Impostazioni</span>
                  </Link>
                </div>
              </nav>
            </div>
          </aside>

          {/* Main Content Area + Footer */}
          <div className="flex-1 flex flex-col min-h-[calc(100vh-64px)]">
            <main className="flex-1 p-8">
              <div className="max-w-7xl mx-auto">
                {children}
              </div>
            </main>
            {/* Footer on all admin pages */}
            <Footer />
          </div>
        </div>
      </div>
    </KPIRefreshContext.Provider>
  );
};

export default AdminDashboardLayout;